import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { pictureForSlot, slotOf } from '../editor/picSlot';
import { assets } from '../editor/storage';
import { shortText } from '../print/rules';
import { toast } from './toast';

/*
 * ช่องใส่รูป → "ใส่รูป" (toolbar chip, src/ui/toolbar.ts): pick a picture; it takes the slot's place, size, turn, shape
 * and group, covering it (the middle kept). One undo step. Refused with the reason: someone else's work, a page still
 * loading, a file that is not a picture or that this browser cannot open (HEIC…), the slot gone meanwhile.
 */
export interface SlotHooks { cannotEdit: () => string | null }

/**
 * A loaded picture into a slot: its place, size, turn, shape, group and opacity, the slot's place in the stack —
 * one undo step (BUG-51). Also used by รูป AI (src/ui/aiImage.ts). Returns a Thai reason when it cannot (someone else's
 * work, a page loading, the slot gone or changed page meanwhile — BUG-31), else null.
 */
export function putInSlot(ed: Editor, history: History, slot: fabric.Object, el: HTMLImageElement, name: string, cannotEdit: () => string | null): string | null {
  const { canvas } = ed;
  const why = cannotEdit();
  if (why) return why;
  const at = canvas.getObjects().indexOf(slot);
  if (at < 0 || !slotOf(slot)) return 'ช่องใส่รูปนี้ไม่อยู่บนหน้าแล้ว (อาจถูกลบ หรือเปลี่ยนหน้าไป) จึงยังไม่ได้ใส่รูป';
  let img: fabric.Image;
  try { img = pictureForSlot(el, slot); }
  catch (e) { return `ใส่รูปไม่ได้: ${e instanceof Error ? e.message : String(e)}`; }
  const g = (slot as { groupId?: string }).groupId;
  img.set({ name: shortText(name, 28) || 'รูปภาพ', opacity: slot.opacity ?? 1 });
  if (g) (img as fabric.Image & { groupId?: string }).groupId = g;
  // one undo step (BUG-51): swapped quietly, recorded once
  history.silently(() => { canvas.remove(slot); canvas.insertAt(img, at, false); });
  canvas.setActiveObject(img);
  canvas.fire('object:modified', { target: img });
  canvas.requestRenderAll();
  return null;
}

export function initSlotFill(ed: Editor, history: History, h: SlotHooks) {
  const { canvas } = ed;
  const input = document.createElement('input');
  input.type = 'file'; input.accept = 'image/*'; input.hidden = true; input.id = 'slotFile';
  document.body.append(input);
  let target: fabric.Object | null = null;

  function fill(slot: fabric.Object) {
    const why = h.cannotEdit();
    if (why) { toast(why, true); return; }
    if ((slot as { locked?: boolean }).locked) { toast('ช่องนี้ล็อกอยู่ ปลดล็อกที่ "เลเยอร์" ก่อน', true); return; }
    target = slot;
    input.value = '';
    input.click();   // in the same tap: iPad opens the file picker only from a finger's own event
  }

  input.onchange = () => {
    const f = input.files?.[0], slot = target;
    input.value = ''; target = null;
    if (!f || !slot) return;
    if (!/^image\//.test(f.type)) { toast(`ไฟล์ "${f.name}" ไม่ใช่รูปภาพ เลือกไฟล์ JPG, PNG หรือ WebP`, true); return; }
    const url = URL.createObjectURL(f);
    const el = new Image();
    el.onload = () => {
      // the page may have changed, or the slot been removed, while the file was opening (putInSlot checks again)
      assets.set(url, f);   // the saver uploads it from here (blob: address)
      const why = putInSlot(ed, history, slot, el, f.name.replace(/\.[^.]+$/, ''), h.cannotEdit);
      if (why) { assets.delete(url); URL.revokeObjectURL(url); toast(why, true); return; }
      toast('ใส่รูปแล้ว · ปรับส่วนที่เห็นได้ที่ "ครอป" · เลิกทำได้');
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      toast(`เปิดรูป "${f.name}" ไม่ได้ เบราว์เซอร์อาจไม่รองรับไฟล์ชนิดนี้ (เช่น HEIC) ลองแปลงเป็น JPG ก่อน`, true);
    };
    el.src = url;
  };

  canvas.on('slot:fill' as never, ((opt: { target?: fabric.Object }) => { if (opt.target) fill(opt.target); }) as never);
  return { fill };
}
